import { parse, startOfDay } from "date-fns";
import { z } from "zod";

import { VideoConferencingProviderEnum } from "@performyard/service-contracts";

import { config } from "../../config";

const combineDateAndTime = (date: Date, time: string): Date => {
	return parse(time, "HH:mm", date);
};

const meetingFormBaseSchema = z.object({
	name: z.string().trim().min(1, "This is a required field").max(280, "Name cannot exceed 280 characters"),
	startDate: z.date({ error: (issue) => (issue.input === undefined ? "This is a required field" : undefined) }),
	endDate: z.date({ error: (issue) => (issue.input === undefined ? "This is a required field" : undefined) }),
	startTime: z
		.string()
		.regex(/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/, "Invalid time format")
		.min(1, "This is a required field"),
	endTime: z
		.string()
		.regex(/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/, "Invalid time format")
		.min(1, "This is a required field"),
	description: z
		.string()
		.trim()
		.max(
			config.meetingDescriptionMaxLength,
			`Descriptions cannot exceed ${config.meetingDescriptionMaxLength} characters`
		)
		.optional()
		.or(z.literal("")),
	carryOver: z.boolean().default(false),
	addToCalendar: z.boolean().default(false),
	// The single provider chosen for this meeting (null = none). One per meeting; rendered today
	// as a checkbox per connected provider, to become a single-select picker with PY-12435.
	videoConferencingProvider: VideoConferencingProviderEnum.nullable().default(null),
	workspaceId: z.string().optional(),
	// Recurrence (recurring meeting modals only)
	repeats: z.enum(["weekday", "weekly", "biweekly", "monthly", "yearly"]).default("weekly"),
	seriesEnds: z.enum(["never", "onDate"]).default("never"),
	seriesEndDate: z.date().optional(),
});

export type RepeatsPreset = z.infer<typeof meetingFormBaseSchema>["repeats"];

const dateOrderingRefinement = {
	handler: (data: z.infer<typeof meetingFormBaseSchema>) => {
		const startDateTime = combineDateAndTime(data.startDate, data.startTime);
		const endDateTime = combineDateAndTime(data.endDate, data.endTime);
		return endDateTime > startDateTime;
	},
	options: {
		message: "End date must occur after the start date",
		path: ["endDate"],
	},
};

export const meetingFormSchema = meetingFormBaseSchema.refine(
	dateOrderingRefinement.handler,
	dateOrderingRefinement.options
);

export type MeetingFormSchema = z.infer<typeof meetingFormSchema>;

export type FormErrors = Partial<Record<keyof MeetingFormSchema, string>>;

export type MeetingValidationErrors = Partial<Record<keyof MeetingFormSchema, { message: string }>>;

export const dashboardMeetingFormSchema = meetingFormBaseSchema
	.extend({ workspaceId: z.string().min(1, "This is a required field") })
	.refine(dateOrderingRefinement.handler, dateOrderingRefinement.options);

const seriesEndDateRequiredRefinement = {
	handler: (data: z.infer<typeof meetingFormBaseSchema>) => data.seriesEnds !== "onDate" || !!data.seriesEndDate,
	options: {
		message: "This is a required field",
		path: ["seriesEndDate"],
	},
};

const seriesEndDateOrderingRefinement = {
	handler: (data: z.infer<typeof meetingFormBaseSchema>) => {
		if (data.seriesEnds !== "onDate" || !data.seriesEndDate) {
			return true;
		}
		// The end date is inclusive — a series may end on the day of its last meeting
		return data.seriesEndDate >= startOfDay(data.startDate);
	},
	options: {
		message: "Series end date cannot be before the start date",
		path: ["seriesEndDate"],
	},
};

const seriesStartNotPastRefinement = {
	handler: (data: z.infer<typeof meetingFormBaseSchema>) => data.startDate >= startOfDay(new Date()),
	options: {
		message: "Start date cannot be in the past",
		path: ["startDate"],
	},
};

export const recurringMeetingFormSchema = meetingFormBaseSchema
	.refine(dateOrderingRefinement.handler, dateOrderingRefinement.options)
	.refine(seriesEndDateRequiredRefinement.handler, seriesEndDateRequiredRefinement.options)
	.refine(seriesEndDateOrderingRefinement.handler, seriesEndDateOrderingRefinement.options);

// An edit prefills the current meeting's start, which may already be under way; only a new series must start today or later.
export const createRecurringMeetingFormSchema = recurringMeetingFormSchema.refine(
	seriesStartNotPastRefinement.handler,
	seriesStartNotPastRefinement.options
);
